import React, { useState } from 'react'
import { useInterval } from 'ahooks'

import { dayjs } from '@/lib/dayjs.ts'
import { cn } from '@/lib/utils.ts'
import { Badge } from '@/components/ui/badge.tsx'
import {
  DocumentAttributeRender,
  isComplexDataType,
} from '@/features/documents/document-attribute-render.tsx'
import { StatusBadge } from '@/features/documents/status-badge.tsx'
import { Document } from '@/features/documents/types.ts'
import { useGetProcessingStepDesc } from '@/routes/_authenticated/documents/-hooks/useGetProcessingStepDesc.ts'
import { DocumentAttributeActions } from '@/routes/_authenticated/documents/$documentId/-components/document-attribute-actions.tsx'

export interface DocumentDetailsProps {
  document?: Document
}

export const DocumentDetails: React.FC<DocumentDetailsProps> = ({
  document,
}) => {
  const processingStepDesc = useGetProcessingStepDesc(document)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  useInterval(
    () => {
      const startTime = document?.processing_at
      const endTime = document?.in_review_at ?? new Date()

      setElapsedSeconds(dayjs(endTime).diff(startTime, 'seconds'))
    },
    document?.status === 'processing' ? 1000 : undefined
  )

  if (!document) return

  return (
    <>
      <dl className="divide-y">
        <div className="border-bottom flex flex-col border-gray-100 py-4 sm:justify-between lg:flex-row">
          <div>
            <dt className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
              Stato
              <StatusBadge className="ml-2" document={document} />
              {document?.status === 'processing' ? (
                <span className="ml-2 text-xs leading-6 text-gray-500">
                  {elapsedSeconds} secondi
                </span>
              ) : null}
            </dt>
            {document.status === 'processing' && (
              <dd className="flex items-center text-xs leading-6 text-gray-500 sm:mt-0">
                {processingStepDesc}
              </dd>
            )}
          </div>
          <div className="mt-4 sm:mt-0">
            <dt className="mb-2 text-sm font-medium leading-6 text-gray-900">
              Caricato il
              <span className="ml-1 mt-1 text-sm leading-6 text-gray-700">
                {dayjs(document.created_at).format('DD/MM/YYYY HH:mm')}
              </span>
            </dt>
          </div>
        </div>
        {document?.status !== 'processing' && (
          <div className="border-bottom border-gray-100 py-4">
            <dt className="mb-2 text-sm font-medium leading-6 text-gray-900">
              Descrizione
            </dt>
            <dd className="mt-1 flex text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              <span className="grow">{document.summary}</span>
            </dd>
          </div>
        )}
      </dl>

      {/*<p className="mb-4 text-sm">{document.summary}</p>*/}

      <dl className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        {document.attributes.map((documentAttribute) => {
          const confidenceScoreVisible =
            document.status === 'in_review' &&
            !isNaN(documentAttribute.confidence_score!) &&
            documentAttribute.confidence_score !== null
          const { level, color } = getConfidenceLevel(
            documentAttribute.confidence_score!
          )

          return (
            <div
              className={cn(
                'border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0',
                isComplexDataType(documentAttribute.attribute.json_schema) &&
                  '!col-span-2'
              )}
              key={documentAttribute.attribute.id}
            >
              <dt className="mb-2 flex items-center justify-between text-sm font-medium leading-6 text-gray-900">
                <div className="flex items-center gap-x-2">
                  {confidenceScoreVisible && (
                    <Badge
                      variant="default"
                      className={`${color} size-4 p-0 px-1 leading-none text-white`}
                    />
                  )}

                  {documentAttribute.attribute.name}
                </div>

                <DocumentAttributeActions
                  documentAttribute={documentAttribute}
                  document={document}
                  level={level}
                />
              </dt>
              <dd className="text-sm leading-6 text-gray-700 sm:mt-2">
                <DocumentAttributeRender
                  attributeJsonSchema={documentAttribute.attribute.json_schema}
                  value={documentAttribute.value}
                />
              </dd>
            </div>
          )
        })}
      </dl>
    </>
  )
}

function getConfidenceLevel(confidence: number) {
  if (confidence >= 90)
    return {
      level: 'Molto Alto',
      color: cn('bg-green-500/80 hover:bg-green-500/80'),
    }
  if (confidence >= 60)
    return {
      level: 'Alto',
      color: cn('bg-lime-500/80 hover:bg-lime-500/80'),
    }
  if (confidence >= 30)
    return {
      level: 'Moderato',
      color: cn('bg-yellow-500/80 hover:bg-yellow-500/80'),
    }
  if (confidence >= 10)
    return {
      level: 'Basso',
      color: cn('bg-orange-500/80 hover:bg-orange-500/80'),
    }
  return {
    level: 'Molto Scarso',
    color: cn('bg-red-500/80 hover:bg-red-500/80'),
  }
}
